import { useState, useEffect, useRef } from "react"
import { Form, Row, Col } from 'react-bootstrap'
import { useNavigate, Link } from "react-router-dom"
import { useQuery, useLazyQuery, useMutation } from "@apollo/client"
import CustomModal from '../../../../components/CustomModal'
import CustomButton from '../../../../components/CustomButton'
import useUser from "../../../../hooks/useUser"

import "./index.scss"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import GET_PROGRAMMES from "../../../../graphql/queries/courseCatalogue/getProgrammes"
import GET_PROGRAMME_FORM from "../../../../graphql/queries/courseCatalogue/getProgrammeForm"

import CREATE_PROGRAMME from '../../../../graphql/mutations/courseCatalogue/createProgramme'
import EDIT_PROGRAMME from '../../../../graphql/mutations/courseCatalogue/editProgramme'
import REMOVE_PROGRAMME from '../../../../graphql/mutations/courseCatalogue/removeProgramme'

import CustomTable from "../Table"
import TableSkeleton from "../Table/components/TableSkeleton"

import toastConfiguration from "../../../../data/toastConfiguration"
import { ToastContainer, toast } from "react-toastify"

import IconButton from "../../../../components/IconButton"
import {
  faPlus,
  faCheck,
  faFloppyDisk,
  faTrash,
  faChevronLeft,
  faUpload
} from "@fortawesome/free-solid-svg-icons"
import COLORS from "../../../../data/colors"

import { useTranslation } from 'react-i18next'

import DynamicForm from '../../../../components/DynamicForm'

import { 
  convertProgrammeStringToNumbers, 
  isDeepEqual, 
  getPatchDiff 
} from '../../../../utils/courseCatalogueManager'


const Programmes = () => {
  const formRef = useRef(null)
  const [loadingComponent, setLoadingComponent] = useState(true)
  const [programmeData, setProgrammeData] = useState([])
  const [programmeForm, setProgrammeForm] = useState([])
  const [selectedProgramme, setSelectedProgramme] = useState({})
  const [tableHeaders, setTableHeaders] = useState([])
  const [columnsPerFieldMap, setColumnsPerFieldMap] = useState({})
  const [formData, setFormData] = useState({})
  const [validated, setValidated] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState("")

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useUser()

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [loadProgrammes, { 
    loading: loadingProgrammes, 
    data: dataProgrammes,
    errorProgrammes,
    refetch: refetchProgrammes }
  ] = useLazyQuery(GET_PROGRAMMES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Programmes data received")

      setProgrammeData(data.programmes)
    },
    onError: (error) => {
      console.error("Error loading Programmes")
    }
  })

  const [loadGetProgrammeForm, { 
    loading: loadingGetProgrammeForm, dataGetProgrammeForm, errorGetProgrammeForm }
  ] = useLazyQuery(GET_PROGRAMME_FORM, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Programme Form data received")

      // Save form into session storage
      sessionStorage.setItem("programmeForm", JSON.stringify(data.getProgrammeForm))

      setProgrammeForm(data.getProgrammeForm)

      // Get table headers list from form display
      const programmeFormArray = Object.entries(data.getProgrammeForm)
      const headers = []
      programmeFormArray.forEach(formItem => {
        if(formItem[1]?.display?.column_display) {
          headers.push(formItem[0])
        }
      })

      // Create columnsPerFieldMap
      const columnsHeaders = headers.reduce((acc, key) => {
        acc[key] = "col-" + headers.length
        return acc
      }, {})

      setColumnsPerFieldMap(columnsHeaders)
      setTableHeaders(headers)

    },
    onError: (error) => {
      console.error("Error loading Programme Form")
    }
  })

  const [callCreateProgramme, { 
    loading: loadingCreateProgramme, dataCreateProgramme }
  ] = useMutation(CREATE_PROGRAMME, {
    onCompleted: (data) => {
      console.log("Programme created")
    },
    onError: (error) => {
      console.error("Error creating Programme")
    }
  })

  const [callEditProgramme, { 
    loading: loadingEditProgramme, dataEditProgramme }
  ] = useMutation(EDIT_PROGRAMME, {
    onCompleted: (data) => {
      console.log("Programme modified")
    },
    onError: (error) => {
      console.error("Error editing Programme")
    }
  })

  const [callRemoveProgramme, { 
    loading: loadingRemoveProgramme, dataRemoveProgramme }
  ] = useMutation(REMOVE_PROGRAMME, {
    onCompleted: (data) => {
      console.log("Programme removed")
    },
    onError: (error) => {
      console.error("Error removing Programme")
    }
  })

  useEffect(() => {

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))

    if(!userHasPermission) {
      navigate('/cc')
      return
    }

    // If userId and backend username don't match, redirects to /cc
    if(user?.userId !== userConnectionStorage?.backendUserName) {
      navigate('/cc')
      return
    }

    // If there are no connections, redirects to /cc
    if(!heiConnectionStorage || !userConnectionStorage) {
      navigate('/cc')
      return
    }

    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    const programmeForm = JSON.parse(sessionStorage.getItem('programmeForm'))

    loadProgrammes({
      variables: {
        schacCode: user?.heiID,
        baseUrl: baseUrl
      }
    })

    if(!programmeForm){
      loadGetProgrammeForm({
        variables: { baseUrl, apiKey }
      })
    } else {
      setProgrammeForm(programmeForm)

      // Get table headers list from form display
      const programmeFormArray = Object.entries(programmeForm)
      const headers = []
      
      programmeFormArray.forEach(formItem => {
        if(formItem[1]?.display?.column_display) {
          headers.push(formItem[0])
        }
      })

      // Create columnsPerFieldMap
      const columnsHeaders = headers.reduce((acc, key) => {
        acc[key] = "col-" + headers.length
        return acc
      }, {})

      setColumnsPerFieldMap(columnsHeaders)
      setTableHeaders(headers)
      /* setLoadingComponent(false) */
    }

  }, [loadProgrammes])

  useEffect(() => {
    if (dataProgrammes?.programmes && programmeForm && Object.keys(programmeForm).length > 0) {
      /* setProgrammeData(dataProgrammes.programmes) */
      setLoadingComponent(false)
    }
  }, [dataProgrammes, programmeForm])

  const handleFormChange = (data) => {
    setFormData(data)
  }

  const formIsValidated = (e) => {
    setValidated(true)
    e.preventDefault()

    if (!formRef.current.checkValidity()) {
      e.stopPropagation()
      return false
    }

    return true
  }

  const handleAdd = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading('Creating Programme', toastConfiguration)

    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey
    const backendHeiUuid = heiConnectionStorage?.backendHeiUuid

    const cleanedData = convertProgrammeStringToNumbers(formData)

    try {
      let result = {}

      result = await callCreateProgramme({
        variables: {
          programmeData: cleanedData,
          baseUrl: baseUrl,
          apiKey: apiKey,
          heiUuid: backendHeiUuid
        }
      })

      setValidated(false)
      if (!result?.errors) {

        toast.update(toastId, {
          ...toastConfiguration,
          render: `Created Programme ${cleanedData.title[0].string}`,
          type: "success",
          isLoading: false,
        })
        const { data: refreshedProgramme } = await refetchProgrammes({
          variables: { baseUrl, apiKey }
        })

        setProgrammeData(refreshedProgramme.programmes)

        setShowModal(false)

      } else {

        console.error("Create Programme failed.")

        toast.update(toastId, {
          ...toastConfiguration,
          render: "Could not create Programme. " + result?.errors,
          type: "error",
          isLoading: false,
        })
      }

    } catch (err) {
      console.error("Create Programme failed:", err)

      toast.update(toastId, {
        ...toastConfiguration,
        render: "Could not create Programme. Try again later.",
        type: "error",
        isLoading: false,
      })
    }

  }

  const handleEdit = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading('Updating Programme', toastConfiguration)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    let cleanedData = convertProgrammeStringToNumbers(formData)
    cleanedData.id = selectedProgramme.id
    cleanedData.uuid = selectedProgramme.uuid
    cleanedData.status = selectedProgramme.status

    // Check if the data has changed
    // If yes, run the query
    // if not, show an error message
    if(!isDeepEqual(selectedProgramme, cleanedData)) {
      try {
        let result = {}

        result = await callEditProgramme({
          variables: {
            programmeData: getPatchDiff(selectedProgramme, cleanedData),
            baseUrl,
            apiKey
          }
        })

        setValidated(false)
        
        if (!result?.errors) {

          toast.update(toastId, {
            ...toastConfiguration,
            render: `Programme ${cleanedData.title[0].string} updated`,
            type: "success",
            isLoading: false,
          })
          
          const { data: refreshedProgramme } = await refetchProgrammes({
            variables: { baseUrl, apiKey }
          })

          setProgrammeData(refreshedProgramme.programmes)

          setShowModal(false)

        } else {

          console.error("Edit Programme failed.")

          toast.update(toastId, {
            ...toastConfiguration,
            render: "Could not edit Programme. " + result?.errors,
            type: "error",
            isLoading: false,
          })
        }

      } catch (err) {
        
        console.error("Edit Programme failed:", err)

        toast.update(toastId, {
          ...toastConfiguration,
          render: "Could not edit Programme. Try again later.",
          type: "error",
          isLoading: false,
        })
      }

    } else {
      toast.update(toastId, {
        ...toastConfiguration,
        render: "There are no changes in the data.",
        type: "error",
        isLoading: false,
      })
    }

  }

  const handleDelete = async (e) => {
    
    setShowModal(false)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    const toastId = toast.loading('Unpublishing Programme...', toastConfiguration)
    
    try {

      const result = await callRemoveProgramme({
       variables: {
          baseUrl: baseUrl,
          apiKey: apiKey,
          programmeUuid: selectedProgramme.id,
        }
      })

      setValidated(false)
      
      if (!result?.errors) {
        toast.update(toastId, {
          ...toastConfiguration,
          render: 'Successfully unpublished the Programme',
          type: "success",
          isLoading: false,
        })
        const { data: refreshedProgramme } = await refetchProgrammes({
          variables: { baseUrl, apiKey }
        })

        setProgrammeData(refreshedProgramme.programmes)
        
        setShowModal(false)

      } else {

        console.error("Unpublish Programme failed.")

        toast.update(toastId, {
          ...toastConfiguration,
          render: "Could not unpublish Programme. " + result?.errors,
          type: "error",
          isLoading: false,
        })
      }
    } catch (err) {

      toast.update(toastId, {
        ...toastConfiguration,
        render: 'Could not unpublish the Programme',
        type: "error",
        isLoading: false,
      })

    }
  }

  return (
    <main className="h-auto text-center mt-4">
      {loadingProgrammes || loadingGetProgrammeForm || loadingComponent ? <TableSkeleton
        columnsPerFieldMap={columnsPerFieldMap}
      /> : errorProgrammes ?
        <div className="text-center text-danger mx-auto">{t('errorMessage')}</div> :
        <>
          <Row className="my-5 display-flex justify-content-between">
            <Col md={2} className="my-4">
              <Link to="/cc" className="goBackLink">
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  color={COLORS.greyText}
                  className="with-icon me-1"
                  id={"smallIcon"}
                />
                {"Go back"}
              </Link>
            </Col>
            <Col md={4} className="my-2">
              <CustomButton 
                buttonType={"primary"} 
                small={true} 
                text={"upload"} 
                hasIcon={true} 
                iconColor={COLORS.white} 
                icon={faUpload} 
                handleClick={(e) => { navigate("/cc/programmes/upload") }}
              />
              <CustomButton 
                type="submit" 
                buttonType={"primary"} 
                text={'add a new programme'} 
                icon={faPlus} 
                handleClick={() => {
                  setSelectedAction("add")
                  setValidated(false)
                  setShowModal(true)
                }} 
                hasIcon 
                styles={{ marginLeft: 10 }} 
                small 
              />
            </Col>
          </Row>
          <CustomTable
            type={"programmes-"}
            columnsPerFieldMap={columnsPerFieldMap}
            hasPagination={false}
            hasFilters={false}
            rowClassName="programmes-listing-table-row"
            headerClassName="programmes-listing-table-header"
            data={dataProgrammes?.programmes}
            tableHead={tableHeaders}
            onClickHandlers={dataProgrammes?.programmes.map(
              ( programme ) =>
              (e) => {
                navigate("/cc/programmes/" + programme.id)
              }
            )}
          >
            {dataProgrammes?.programmes?.map((programme) => (
              <div
                key={programme?.id}
                className={`py-3 px-2 justify-content-end justify-content-lg-around align-items-center bg-none bg-md-light button-triplet-requests flex-grow-1 d-flex gap-3 gap-lg-1`}
              >
                {/* <Toggle/> */}
                <IconButton
                  variant="edit"
                  title={'Save Changes'}
                  onClick={() => {
                    setSelectedAction("edit")
                    setShowModal(true)
                    setSelectedProgramme(programme)
                  }}
                />
                <IconButton
                  variant="delete"
                  onClick={() => {
                    setSelectedAction("delete")
                    setShowModal(true)
                    setSelectedProgramme(programme)
                  }}
                  title={'Delete'}
                />
              </div>
            ))}
          </CustomTable>
          <CustomModal
            show={showModal}
            handleClose={() => {
              setShowModal(false)
              setValidated(false)
            }}
            disabledCancelButton={
              loadingCreateProgramme || loadingEditProgramme
            }
            body={
              (selectedAction === "add" || selectedAction === "edit") && (
                <div>
                  
                  <Form ref={formRef} noValidate validated={validated}>
                    <DynamicForm 
                      schema={programmeForm}
                      onChange={handleFormChange}
                      defaultValues={selectedAction === 'edit' ? selectedProgramme : {}}
                    />
                  </Form>
                  
                </div>
              )
            }
            button={
              selectedAction === "add" ? ( // new
                <CustomButton
                  buttonType={"primary"}
                  disabled={loadingCreateProgramme}
                  text={"Add"}
                  icon={faCheck}
                  hasIcon={true}
                  iconColor={COLORS.white}
                  handleClick={handleAdd}
                />
              ) : selectedAction === "edit" ? ( // edit
                <CustomButton
                  buttonType={"primary"}
                  disabled={loadingEditProgramme}
                  text={'Save Changes'}
                  icon={faFloppyDisk}
                  hasIcon={true}
                  iconColor={COLORS.white}
                  handleClick={handleEdit}
                />
              ) : (
                // delete
                <CustomButton
                  buttonType={"decline"}
                  text={'Unpublish'}
                  iconColor={COLORS.white}
                  handleClick={handleDelete}
                />
              )
            }
            title={
              selectedAction === "add" ? (
                'Add a new Programme'
              ) : selectedAction === "edit" ? (
                'Edit an existing Programme'
              ) : (
                <>
                  {'You are about to '}
                  <span className="accounts-and-access-delete-text-on-modal">
                    {'unpublish'}
                  </span>{" "}
                  {'a Programme'}
                </>
              )
            }
            subtitle={
              selectedAction === "add" ? (
                'Add the necessary information to add a new Programme'
              ) : selectedAction === "edit" ? (
                'Add the necessary information to edit a new Programme'
              ) : ('The Programme will no longer be visible. This action cannot be undone.')
            }
            hasIcon
            fullscreen
          />
        </>}
          <ToastContainer
            position="bottom-left"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
    </main>
  )
}

export default Programmes
